import React, { useState } from 'react';
import { FaCheck } from 'react-icons/fa';
import { TIERS, TIER_ORDER, formatGuestCap, formatPhotoCap, weddingPlannerIncludedLine } from '../services/pricingTiers';
import './PricingModal.css';

const FEATURES = [
  'Guests upload with no app or signup',
  'Built-in camera with filters and timer',
  '30-day shared gallery',
  'Download every photo as one ZIP',
  'QR code and shareable link included',
];

const POPULAR_TIER = 'growth';

// Display-only taglines; pricing data itself lives in pricingTiers.js.
const EYEBROWS = {
  free: 'Try it out',
  starter: 'Small gatherings',
  growth: 'The classic wedding',
  unlimited: 'Every last guest',
};

const HEADING_WORDS = 'Create Your Event'.split(' ');

export function PricingModal({ isOpen, onClose, onSelectPlan, freeTierUsed }) {
  const [loadingTier, setLoadingTier] = useState(null);

  const handleSelect = async (tierKey) => {
    setLoadingTier(tierKey);
    try {
      await onSelectPlan(tierKey);
    } catch (error) {
      // onSelectPlan is expected to surface its own error toast
    } finally {
      setLoadingTier(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="pricing-modal-overlay">
      <div className="pricing-modal">
        <button className="modal-close" onClick={onClose}>
          ✕
        </button>

        <h1 aria-label="Create Your Event">
          {HEADING_WORDS.map((word, i) => (
            <span className="word-mask" key={word} aria-hidden="true">
              <span className="word" style={{ '--i': i }}>
                {word}
              </span>
            </span>
          ))}
        </h1>
        <p className="subtitle">Priced by guest count, pay once, no subscription</p>

        <div className="pricing-grid">
          {TIER_ORDER.map((tierKey, index) => {
            const tier = TIERS[tierKey];
            const isFree = tierKey === 'free';
            const isPopular = tierKey === POPULAR_TIER;
            const freeAlreadyUsed = isFree && freeTierUsed;

            return (
              <div
                key={tierKey}
                className={`pricing-card${isPopular ? ' featured' : ''}`}
                style={{ '--i': index }}
              >
                {isPopular && <div className="badge">MOST POPULAR</div>}

                <p className="eyebrow">{EYEBROWS[tierKey]}</p>
                <h2>{tier.name}</h2>
                <div className="price">
                  {tier.display}
                  {!isFree && <span className="period">/event</span>}
                </div>
                <p className="plan-desc">
                  {freeAlreadyUsed
                    ? "You've already used your one free event"
                    : `${formatGuestCap(tier.guestCap)} · ${formatPhotoCap(tier.photosPerGuest)}`}
                </p>

                <ul className="features">
                  {[...FEATURES, weddingPlannerIncludedLine(tierKey)].filter(Boolean).map((feature) => (
                    <li key={feature}>
                      <span className="check-badge">
                        <FaCheck className="check" />
                      </span>
                      {feature}
                    </li>
                  ))}
                </ul>

                <button
                  className={`plan-btn ${isFree ? 'free-btn' : 'paid-btn'}`}
                  onClick={() => handleSelect(tierKey)}
                  disabled={loadingTier !== null || freeAlreadyUsed}
                >
                  {loadingTier === tierKey
                    ? 'Processing...'
                    : freeAlreadyUsed
                    ? 'Already Used'
                    : isFree
                    ? 'Use Free Plan'
                    : `Choose ${tier.name}`}
                </button>
              </div>
            );
          })}
        </div>

        <div className="pricing-footer">
          <p>Events stay live for 30 days after your event date</p>
          <p>Paid plans are processed securely with Paystack</p>
        </div>
      </div>
    </div>
  );
}
